import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { guid } from 'app/shared/lib/guid';

interface IService {
    desc: string | null,
    medicalCodes?: IMedicalCode[]
}

interface IMedicalCode {
    code?: string | null,
    type?: string | null,
    desc?: string | null
}

interface IOutcomeNote {
    user: string | null,
    dateTime: string | null,
    note: string | null
}

interface ISelectedLos {
    days: string | null,
    type: string | null,
    diagnosis: string | null
}

@autoinject()
export class IqcSummaryView extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull()
    protected reviewXml: string | null = null;
    protected summary: Document | null = null;
    protected showOutcomeNotes: boolean = false;
    protected showUtilizationBenchmarks: boolean = false;
    protected selectedLos: ISelectedLos | null = null;
    protected outcomeNotes: IOutcomeNote[] = [];
    protected decisionReasons: string | null = null;
    protected recommendedServices: IService[] = [];
    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(...rest: any[]) {
        super(...rest);
    }

    ////****************** Events ***************************************************////
    protected reviewXmlChanged(): void {
        if(this.reviewXml) {
            const parser = new DOMParser();
            this.summary = parser.parseFromString(this.reviewXml, "text/xml");

            this.parseUtilizationBenchmarks();
            this.parseDecisionReasons();
            this.parseOutcomeNotes();
            this.parseRecommendedServices();
        }
    }

    ////****************** Functions ************************************************////
    private parseDecisionReasons(): void {
        if(this.summary == null) return;

        const decisionReasonsNode = this.summary.getElementsByTagName('Review')[0].getElementsByTagName('DecisionReasons');
        if(decisionReasonsNode == null || decisionReasonsNode.length === 0) return;

        const textSummaryDescNode = decisionReasonsNode[0].getElementsByTagName('TextDescSummary');
        if(textSummaryDescNode == null || textSummaryDescNode.length === 0) return;

        this.decisionReasons = textSummaryDescNode[0].innerHTML;
    }

    private parseOutcomeNotes(): void {
        this.outcomeNotes = [];
        this.showOutcomeNotes = false;

        if(!this.summary) return;

        const reviewNode = this.summary.getElementsByTagName('Review')[0];
        if(!reviewNode) return;

        const outcomesNode = reviewNode.getElementsByTagName('Outcomes')[0];
        if(!outcomesNode) return;
        this.showOutcomeNotes = true;

        const outcomeNoteNodes = outcomesNode.getElementsByTagName('OutcomeNote');
        if(!outcomeNoteNodes || outcomeNoteNodes.length === 0) return;

        for(const outcomeNoteNode of outcomeNoteNodes) {
            this.outcomeNotes.push({
                user: outcomeNoteNode.getAttribute('User'),
                dateTime: outcomeNoteNode.getAttribute('DateTime'),
                note: (outcomeNoteNode.textContent || '').trim() || null
            });
        }
    }

    private parseRecommendedServices(): void {
        if(this.summary == null) return;

        const servicesNode = this.summary.getElementsByTagName('Review')[0].getElementsByTagName('Services');
        if(servicesNode == null || servicesNode.length === 0) return;

        const recommendedServicesNode = servicesNode[0].getElementsByTagName('RecommendedServices');
        if(recommendedServicesNode == null || recommendedServicesNode.length === 0) return;

        const serviceNodes = recommendedServicesNode[0].getElementsByTagName('Service');
        if(serviceNodes == null || serviceNodes.length === 0) return;

        for(const serviceNode of serviceNodes) {
            let serviceDesc = serviceNode.getAttribute('ServiceDesc');

            const indicators = serviceNode.getAttribute('Indicators');
            if(indicators != null && indicators.length > 0) serviceDesc += ' - ' + indicators;

            const s: IService = { desc: serviceDesc };

            const medicalCodeNodes = serviceNode.getElementsByTagName('MedicalCode');
            if(medicalCodeNodes == null || medicalCodeNodes.length === 0) {
                this.recommendedServices.push(s);
                return;
            }

            s.medicalCodes = [];

            for(const medicalCodeNode of medicalCodeNodes) {
                if(medicalCodeNode.getAttribute('AuthorizationType') == 'P') s.medicalCodes?.push({ code: medicalCodeNode.getAttribute('Code'), type: medicalCodeNode.getAttribute('Type'), desc: medicalCodeNode.getAttribute('Desc') });
            }

            this.recommendedServices.push(s);
        }
    }

    private parseUtilizationBenchmarks(): void {
        this.showUtilizationBenchmarks = false;
        this.selectedLos = null;

        if(!this.summary) return;

        const reviewNode = this.summary.getElementsByTagName('Review')[0];
        if(!reviewNode) return;

        const benchmarkLosNode = reviewNode.getElementsByTagName('BenchmarkLOS')[0];
        if(!benchmarkLosNode) return;

        const losNodes = benchmarkLosNode.getElementsByTagName('LOS');
        if(!losNodes || losNodes.length === 0) return;

        let selectedLosNode: Element | null = null;
        for(const losNode of losNodes) {
            if((losNode.getAttribute('Selected') || '').toLowerCase() === 'true') {
                selectedLosNode = losNode;
                break;
            }
        }

        if(!selectedLosNode) return;

        this.selectedLos = {
            days: selectedLosNode.getAttribute('Days'),
            type: selectedLosNode.getAttribute('TypeDesc'),
            diagnosis: selectedLosNode.getAttribute('Diagnosis')
        };
        this.showUtilizationBenchmarks = true;
    }
}
